import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import {
  SettingsOutlined as SettingsIcon,
  AccountTreeOutlined as SchemaDiagramIcon,
  TableChartOutlined as SchemaTableIcon,
  PlayArrowOutlined as PlayArrowIcon,
  AltRouteOutlined as ForkIcon,
  LinkOutlined as ConnectionIcon,
  PhotoLibraryOutlined as SnapshotsIcon,
  PeopleOutlined as UsersIcon,
  ScienceOutlined as SeedPlanIcon,
  CloudDownloadOutlined as DownstreamIcon,
  TuneOutlined as ParametersIcon,
} from '@mui/icons-material'
import {
  getSyntheticDatabaseConnectionNavSubItems,
  getSyntheticDatabaseNavItems,
  getSyntheticDatabaseSchemaNavSubItems,
  isOnSyntheticDatabaseConnection,
  isOnSyntheticDatabaseSchema,
  isSyntheticDatabaseNavItemActive,
  syntheticDatabaseConnectionNavItemPath,
  syntheticDatabaseSchemaNavItemPath,
  type SyntheticDatabaseNavItem,
  type SyntheticDatabaseNavItemKind,
} from '../../utils/syntheticDatabaseContextNav'
import {
  isDatabaseConnectionSubItemActive,
  isDatabaseSchemaSubItemActive,
} from '../../utils/databaseContextNav'
import { NavItem } from './sidebarPrimitives'

function iconForKind(kind: SyntheticDatabaseNavItemKind): React.ReactNode {
  switch (kind) {
    case 'seed_plan':
      return <SeedPlanIcon />
    case 'general':
      return <SettingsIcon />
    case 'users':
      return <UsersIcon />
    case 'preview_queries':
      return <PlayArrowIcon />
    case 'snapshots':
      return <SnapshotsIcon />
    case 'forks':
      return <ForkIcon />
    case 'schema':
      return <SchemaDiagramIcon />
    case 'connection':
      return <ConnectionIcon />
    default:
      return <SettingsIcon />
  }
}

function renderNavItem(
  item: SyntheticDatabaseNavItem,
  databaseId: string,
  pathname: string,
  go: (path: string) => void,
) {
  return (
    <NavItem
      key={`${item.kind}-${item.path}`}
      text={item.label}
      icon={iconForKind(item.kind)}
      active={isSyntheticDatabaseNavItemActive(pathname, databaseId, item)}
      onClick={() => go(item.path)}
    />
  )
}

export default function SyntheticDatabaseContextNav({
  databaseId,
  onNavigate,
}: {
  databaseId: string
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()

  const items = getSyntheticDatabaseNavItems(databaseId)
  const schemaSubItems = getSyntheticDatabaseSchemaNavSubItems(databaseId)
  const connectionSubItems = getSyntheticDatabaseConnectionNavSubItems(databaseId)
  const schemaPath = syntheticDatabaseSchemaNavItemPath(databaseId)
  const connectionPath = syntheticDatabaseConnectionNavItemPath(databaseId)

  // Seed plan + General, then Schema group, then the rest, then Connection group.
  const beforeSchema = items.filter((item) => item.kind === 'seed_plan' || item.kind === 'general')
  const afterSchema = items.filter((item) => item.kind !== 'seed_plan' && item.kind !== 'general')

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  const onSchemaRoute = isOnSyntheticDatabaseSchema(location.pathname, databaseId)
  const onConnectionRoute = isOnSyntheticDatabaseConnection(location.pathname, databaseId)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Box sx={{ flex: 1 }}>
        {beforeSchema.map((item) => renderNavItem(item, databaseId, location.pathname, go))}

        <NavItem
          text="Schema"
          icon={<SchemaDiagramIcon />}
          active={onSchemaRoute}
          onClick={() => go(schemaPath)}
        />
        {onSchemaRoute ? (
          <Box sx={{ ml: 1.5, borderLeft: '1px solid var(--vs-border-subtle)', pl: 0.5 }}>
            {schemaSubItems.map((sub) => (
              <NavItem
                key={sub.view}
                text={sub.label}
                icon={
                  sub.view === 'table' ? (
                    <SchemaTableIcon />
                  ) : (
                    <SchemaDiagramIcon />
                  )
                }
                active={isDatabaseSchemaSubItemActive(location.pathname, databaseId, sub.view)}
                onClick={() => go(sub.path)}
                pl={1.5}
                fontSize="0.8125rem"
              />
            ))}
          </Box>
        ) : null}

        {afterSchema.map((item) => renderNavItem(item, databaseId, location.pathname, go))}

        <NavItem
          text="Connection"
          icon={<ConnectionIcon />}
          active={onConnectionRoute}
          onClick={() => go(connectionPath)}
        />
        {onConnectionRoute ? (
          <Box sx={{ ml: 1.5, borderLeft: '1px solid var(--vs-border-subtle)', pl: 0.5 }}>
            {connectionSubItems.map((sub) => (
              <NavItem
                key={sub.view}
                text={sub.label}
                icon={
                  sub.view === 'parameters' ? (
                    <ParametersIcon />
                  ) : (
                    <DownstreamIcon />
                  )
                }
                active={isDatabaseConnectionSubItemActive(location.pathname, databaseId, sub.view)}
                onClick={() => go(sub.path)}
                pl={1.5}
                fontSize="0.8125rem"
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
